/**
 * My Claim Status — /my-claims
 * User dashboard of every union claim they've submitted.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ArrowLeft, ShieldCheck, Clock3, XCircle, MessageCircleWarning, BadgeCheck, Mail } from "lucide-react";

const STATUS_META = {
  pending:        { label: "Under review",  color: "#F5B82E", Icon: Clock3 },
  info_requested: { label: "Info needed",   color: "#F59E0B", Icon: MessageCircleWarning },
  approved:       { label: "Approved",      color: "#22C55E", Icon: BadgeCheck },
  rejected:       { label: "Rejected",      color: "#EF4444", Icon: XCircle },
};

export default function MyClaims() {
  const [rows, setRows]     = useState([]);
  const [loading, setL]     = useState(true);
  const [onboardingMail, setM] = useState("onboarding@callsheet.app");

  useEffect(() => {
    (async () => {
      try {
        const [{ data }, { data: meta }] = await Promise.all([
          api.get(`/union-claims/mine`),
          api.get(`/union-claims/meta`),
        ]);
        setRows(data.claims);
        setM(meta.onboarding_email);
      } finally { setL(false); }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0c] text-white" data-testid="my-claims-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/associations"
          className="text-[10px] tracking-[0.25em] uppercase text-white/55 hover:text-white inline-flex items-center gap-1.5"
          data-testid="my-claims-back">
          <ArrowLeft size={12} /> Directory
        </Link>
        <div className="mt-3 text-[10px] tracking-[0.32em] uppercase text-fuchsia-400">
          My Union Claims
        </div>
        <h1 className="font-display text-3xl uppercase tracking-tight">Claim status</h1>

        {loading && (
          <div className="mt-8 text-center text-white/40 uppercase tracking-[0.3em] text-xs">Loading…</div>
        )}
        {!loading && rows.length === 0 && (
          <div className="mt-8 rounded-2xl border border-white/10 p-8 text-center" data-testid="my-claims-empty">
            <ShieldCheck size={24} className="mx-auto text-white/40" />
            <div className="mt-3 text-sm text-white/60">You haven't submitted any union claims yet.</div>
            <Link to="/associations"
              className="mt-4 inline-block text-[11px] uppercase tracking-[0.24em] px-4 py-2 rounded-full border border-white/20 hover:border-fuchsia-400 hover:text-fuchsia-300">
              Browse associations
            </Link>
          </div>
        )}
        {!loading && rows.length > 0 && (
          <div className="mt-6 space-y-3">
            {rows.map(c => {
              const meta = STATUS_META[c.status] || STATUS_META.pending;
              const Icon = meta.Icon;
              return (
                <div key={c.id}
                  className="rounded-2xl border p-5"
                  style={{ borderColor: `${meta.color}30`, background: `${meta.color}08` }}
                  data-testid={`my-claim-row-${c.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <Link to={`/associations/${c.association_id}`}
                        className="font-display text-xl uppercase tracking-tight hover:underline">
                        {c.association_id}
                      </Link>
                      <div className="text-[10px] uppercase tracking-[0.24em] text-white/50 mt-0.5">
                        {c.designation} · submitted {new Date(c.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.22em]"
                      style={{ color: meta.color, borderWidth: 1, borderColor: `${meta.color}55`, background: `${meta.color}15` }}
                      data-testid={`my-claim-status-${c.id}`}>
                      <Icon size={11} /> {meta.label}
                    </span>
                  </div>
                  {c.admin_note && c.status === "info_requested" && (
                    <div className="mt-3 border-l-2 pl-3 text-sm text-amber-100"
                      style={{ borderColor: `${meta.color}80` }}>
                      <div className="text-[10px] uppercase tracking-[0.22em] text-amber-300 mb-1">
                        Onboarding team says
                      </div>
                      {c.admin_note}
                    </div>
                  )}
                  {c.rejection_reason && c.status === "rejected" && (
                    <div className="mt-3 border-l-2 border-red-500/40 pl-3 text-sm text-red-100">
                      <div className="text-[10px] uppercase tracking-[0.22em] text-red-300 mb-1">Reason</div>
                      {c.rejection_reason}
                    </div>
                  )}
                  {c.status === "approved" && (
                    <Link to={`/associations/${c.association_id}/analytics`}
                      className="mt-3 inline-block text-[10px] uppercase tracking-[0.22em] px-3 py-1.5 rounded-full"
                      style={{ background: meta.color, color: "#000" }}
                      data-testid={`my-claim-open-analytics-${c.id}`}>
                      Open my union's analytics →
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-10 text-[11px] text-white/45 text-center flex items-center justify-center gap-1.5">
          <Mail size={11} /> Need handholding? Write to{" "}
          <a href={`mailto:${onboardingMail}`} className="text-fuchsia-400 hover:text-fuchsia-300"
            data-testid="my-claims-onboarding-mail">
            {onboardingMail}
          </a>
        </div>
      </div>
    </div>
  );
}
